import * as Sentry from "@sentry/react";
import { AxiosError, AxiosRequestConfig } from "axios";
import { now } from "common";
import dayjs from "dayjs";
import { pick } from "lodash";
import { useQuery, UseQueryOptions } from "react-query";
import { Delegate, Delegator, Secrets, Seller, User, UserType } from "types";
import { api } from "../api/api";
import { AuthMethod } from "../auth/authMethod";
import { isXolaAdmin } from "../models/User";
import { useAccountStore } from "../stores/accountStore";
import { isOAuthSession, OAuthSessionStatus, useAuthStore } from "../stores/authStore";
import { useDashboardFilterStore } from "../stores/dashboardFilterStore";
import { Response } from "../types/Response";
import { populateResource } from "./helper";
import { useResource } from "./useResource";

const queryParams = new URLSearchParams(location.search);

interface AccountValues {
    email: string;
    password: string;
    shouldRemember: boolean;
}

export interface AccountState {
    user: User;
    delegators: Delegator[];
    seller?: Seller;
    delegates?: Delegate[];
    secrets?: Secrets;
    shouldRemember?: boolean;
}

export const useAccount = (values?: AccountValues, options?: UseQueryOptions<AccountState, AxiosError>) => {
    const sellerId = useAccountStore((state) => state.seller);
    const setSeller = useAccountStore((state) => state.setSeller);
    const rememberMe = useAccountStore((state) => state.rememberMe);
    const apiKey = useAccountStore((state) => state.apiKey);
    const setApiKey = useAccountStore((state) => state.setApiKey);
    const authMethod = useAuthStore((state) => state.method);
    const authStatus = useAuthStore((state) => state.status);
    const setDate = useDashboardFilterStore((state) => state.setDate);

    const hasSubmittedCredentials = Boolean(values?.email && values?.password);

    const config: AxiosRequestConfig = {
        ignoreUnauthorizedErrorInterceptor: true,
    };

    if (hasSubmittedCredentials) {
        config.auth = { username: values!.email, password: values!.password };
    }

    // An OAuth session has no API key to gate on: it is ready once a token is held.
    const isEnabled =
        authMethod === AuthMethod.OAUTH
            ? authStatus === OAuthSessionStatus.AUTHENTICATED
            : hasSubmittedCredentials || Boolean(apiKey);

    const queryResult = useQuery<any, AxiosError, AccountState>(
        ["account"],
        async () => {
            Sentry.configureScope((scope) => scope.setUser(null)); // clear out any pre-created user in Sentry session
            let { data: user } = await api.get<User>("/users/me", config);

            Sentry.setUser({
                id: user.id,
                name: user.name || "Unknown",
                email: user.email,
                ip_address: "{{auto}}",
                user: { ...pick(user, ["id", "name", "email", "type"]), roles: user.roles.join(",") },
            });
            Sentry.setTag("user_name", user.name);
            Sentry.setTag("is_xola_admin", isXolaAdmin(user));

            // No travelers allowed till we build the traveler app
            if (user.type === UserType.TRAVELER) {
                throw new Error("Sorry, you don't have access to this app yet");
            }

            if (user.type !== UserType.SELLER && user.type !== UserType.USER) {
                throw new Error("Unsupported user type");
            }

            // Only the login form needs this: `setApiKey` has not run yet, so the store is empty and
            // the interceptor would send the calls below with no credential at all.
            const headers = isOAuthSession() ? {} : { "X-API-KEY": user.apiKey! };

            // Store the api key and seller ID for any subsequent request.
            // If we are loggin via API Key, we will check if rememberMe is passed via hash / already saved login is there
            // Incase we are loggin via login page, then we should check if values.shouldRemember is passed
            let shouldRemember = rememberMe;
            if (hasSubmittedCredentials) {
                shouldRemember = values?.shouldRemember ?? false;
            }

            const isImpersonating = isXolaAdmin(user) && Boolean(sellerId);

            // Fetch the seller accounts that are delegated to this user
            const delegators = (await getDelegators(user, headers, isImpersonating)) ?? [];

            let userData: AccountState = {
                user,
                delegators: delegators ?? [], // The list of sellers that have given this user access
                shouldRemember,
            };

            const { selectedSeller, user: userNew } =
                delegators.length <= 1 || sellerId
                    ? await getDelegatedSeller(user, delegators, sellerId, isImpersonating, config)
                    : { selectedSeller: undefined, user };
            user = userNew;

            if (selectedSeller) {
                let seller;
                try {
                    const { data } = await api.get<Seller>(`/sellers/${selectedSeller.id}`, { headers });
                    seller = data;
                } catch {
                    if (user.id === selectedSeller.id) {
                        throw new Error("Your user is not a seller account, please choose another account.");
                    }

                    throw new Error(`Could not find seller with ID ${selectedSeller.id}`);
                }

                populateResource("sellers", seller);
                // Now we have the timezone, update the date.
                dayjs.tz.setDefault(seller.timezoneName);
                setDate(queryParams.get("date") ? dayjs.tz(queryParams.get("date")) : now());

                if (!isOAuthSession()) {
                    setApiKey(user.apiKey!, shouldRemember && !isXolaAdmin(user));
                }

                setSeller(selectedSeller.id, shouldRemember && !isXolaAdmin(user));
                const { data: secrets } = await api.get<Secrets>(`/sellers/${selectedSeller.id}/secrets`, { headers });

                userData = { ...userData, user, secrets, delegates: seller.delegates };
            }

            return userData;
        },
        {
            enabled: isEnabled,
            ...options,
        },
    );

    const { data } = queryResult;
    return { ...queryResult, data };
};

const getDelegators = async (user: User, headers: any, isImpersonating: boolean) => {
    if (user.type !== UserType.SELLER) {
        const { data: delegators } = await api.get<Response<Delegator, any>>("/delegators?limit=500", { headers });

        if (user.type === UserType.USER && delegators.data.length === 0 && !isImpersonating) {
            throw new Error(
                isXolaAdmin(user)
                    ? "You are not delegated to any sellers."
                    : "Sorry, you don't have access to this app",
            );
        }

        return delegators.data;
    }

    return [];
};

const getDelegatedSeller = async (
    user: User,
    delegators: Delegator[],
    sellerId: ID | null,
    isImpersonating: boolean,
    config: any,
) => {
    if (isImpersonating) {
        return { selectedSeller: { id: sellerId! }, user };
    }

    if (user.type === UserType.SELLER) {
        return { selectedSeller: user, user };
    }

    const selectedSeller = sellerId ? delegators.find((delegator) => sellerId === delegator.id) : delegators[0];

    // Refetch user again with seller id in case of seller delegators login
    if (selectedSeller?.id && !sellerId) {
        const params = { ...config, params: { seller: selectedSeller.id } };
        const { data } = await api.get<User>("/users/me", params);
        return { selectedSeller, user: data };
    }

    return { selectedSeller, user };
};

export const useSeller = (): Seller => {
    const { seller } = useAccountStore();
    const { data } = useResource<Seller>(["sellers", seller]);
    return data!;
};

export const useUser = (): User => {
    const { data } = useAccount();

    if (!data?.user) {
        throw new Error("You're calling this very early, the user is not available yet");
    }

    return data.user;
};

export const useSecrets = (): Secrets => {
    const { data } = useAccount();
    // Date will not contain secrets only when we show account selector modal and will always have when a seller gets loaded.
    return data!.secrets!;
};
